import { Dispatch } from "redux";
import { User, DistributionList, Meta, Links } from "typings";
import { Users, DistributionLists } from "@pacerpro/api";
import { RootState } from ".";

type DrawerUsersPayload = {
  data: User[];
  meta?: Meta;
  links?: Links;
  included: any[];
};

type DrawerDistributionListsPayload = {
  data: DistributionList[];
  meta?: Meta;
  links?: Links;
  included: any[];
};

const noop = () => {};

/////////////////////////////////////////////////
// DRAWER CONSTANTS
/////////////////////////////////////////////////

export const LOAD_DRAWER_USERS = "LOAD_DRAWER_USERS";
export const LOAD_MORE_DRAWER_USERS = "LOAD_MORE_DRAWER_USERS";
export const LOAD_DRAWER_DISTRIBUTION_LISTS = "LOAD_DRAWER_DISTRIBUTION_LISTS";
export const LOAD_MORE_DRAWER_DISTRIBUTION_LISTS =
  "LOAD_MORE_DRAWER_DISTRIBUTION_LISTS";
export const SET_DRAWER_USERS_LOADING = "SET_DRAWER_USERS_LOADING";
export const SET_DRAWER_USERS_PAGE = "SET_DRAWER_USERS_PAGE";
export const SET_DRAWER_DISTRIBUTION_LISTS_LOADING =
  "SET_DRAWER_DISTRIBUTION_LISTS_LOADING";
export const SET_DRAWER_DISTRIBUTION_LISTS_PAGE =
  "SET_DRAWER_DISTRIBUTION_LISTS_PAGE";

/////////////////////////////////////////////////
// DRAWER  ACTIONS
/////////////////////////////////////////////////

export const loadDrawerUsers = ({
  data = [],
  included = [],
  meta,
  links,
}: DrawerUsersPayload) => ({
  type: LOAD_DRAWER_USERS,
  payload: { data, included, meta, links },
});

export const loadMoreDrawerUsers = ({
  data = [],
  included = [],
  links,
}: DrawerUsersPayload) => ({
  type: LOAD_MORE_DRAWER_USERS,
  payload: { data, included, links },
});

export const loadDrawerDistributionLists = ({
  data = [],
  included = [],
  meta,
  links,
}: DrawerDistributionListsPayload) => ({
  type: LOAD_DRAWER_DISTRIBUTION_LISTS,
  payload: { data, included, meta, links },
});

export const loadMoreDrawerDistributionLists = ({
  data = [],
  included = [],
  links,
}: DrawerDistributionListsPayload) => ({
  type: LOAD_MORE_DRAWER_DISTRIBUTION_LISTS,
  payload: { data, included, links },
});

export const setDrawerUsersLoading = (payload: boolean) => ({
  type: SET_DRAWER_USERS_LOADING,
  payload,
});

export const setDrawerDistributionListsLoading = (payload: boolean) => ({
  type: SET_DRAWER_DISTRIBUTION_LISTS_LOADING,
  payload,
});

export const setDrawerUsersPage = (payload: number) => ({
  type: SET_DRAWER_USERS_PAGE,
  payload,
});

export const setDrawerDistributionListsPage = (payload: number) => ({
  type: SET_DRAWER_DISTRIBUTION_LISTS_PAGE,
  payload,
});

export const loadMoreUserRows = (options: {
  include?: string[];
  filter: { [key: string]: string | undefined };
  size: number;
}) => async (dispatch: Dispatch, getState: () => RootState) => {
  const state = getState();
  const page = state.drawer.usersPage;
  const pageCount = state.drawer.users.meta.pageCount;
  const isLoading = state.drawer.usersLoading;
  if (isLoading) {
    return new Promise(noop);
  }
  if (page === pageCount) {
    return new Promise(noop);
  }
  try {
    dispatch(setDrawerUsersLoading(true));
    const usersResponse = await Users.getAll({
      ...options,
      number: page + 1,
    });
    dispatch(loadMoreDrawerUsers(usersResponse.data));
    dispatch(setDrawerUsersLoading(false));
    dispatch(setDrawerUsersPage(page + 1));
  } catch (error) {
    console.error(error);
  }
};

export const loadMoreDistributionListRows = (options: {
  include?: string[];
  filter: { [key: string]: string | undefined };
  size: number;
}) => async (dispatch: Dispatch, getState: () => RootState) => {
  const state = getState();
  const page = state.drawer.distributionListsPage;
  const pageCount = state.drawer.distributionLists.meta.pageCount;
  const isLoading = state.drawer.distributionListsLoading;
  if (isLoading) {
    return new Promise(noop);
  }
  if (page === pageCount) {
    return new Promise(noop);
  }
  try {
    dispatch(setDrawerDistributionListsLoading(true));
    const distributionListsResponse = await DistributionLists.getAll({
      ...options,
      number: page + 1,
    });
    dispatch(loadMoreDrawerDistributionLists(distributionListsResponse.data));
    dispatch(setDrawerDistributionListsLoading(false));
    dispatch(setDrawerDistributionListsPage(page + 1));
  } catch (error) {
    console.error(error);
  }
};

export type DrawerAction =
  | {
      type: typeof LOAD_DRAWER_USERS;
      payload: DrawerUsersPayload;
    }
  | {
      type: typeof LOAD_MORE_DRAWER_USERS;
      payload: DrawerUsersPayload;
    }
  | {
      type: typeof LOAD_DRAWER_DISTRIBUTION_LISTS;
      payload: DrawerDistributionListsPayload;
    }
  | {
      type: typeof LOAD_MORE_DRAWER_DISTRIBUTION_LISTS;
      payload: DrawerDistributionListsPayload;
    }
  | {
      type: typeof SET_DRAWER_USERS_LOADING;
      payload: boolean;
    }
  | {
      type: typeof SET_DRAWER_DISTRIBUTION_LISTS_LOADING;
      payload: boolean;
    }
  | {
      type: typeof SET_DRAWER_USERS_PAGE;
      payload: number;
    }
  | {
      type: typeof SET_DRAWER_DISTRIBUTION_LISTS_PAGE;
      payload: number;
    };

/////////////////////////////////////////////////
// DRAWER REDUCER
/////////////////////////////////////////////////

export interface DrawerState {
  usersPage: number;
  usersLoading: boolean;
  users: {
    data: User[];
    meta: Meta;
    links?: Links;
    included: any[];
  };
  distributionListsPage: number;
  distributionListsLoading: boolean;
  distributionLists: {
    data: DistributionList[];
    meta: Meta;
    links?: Links;
    included: any[];
  };
}

const initialState: DrawerState = {
  usersPage: 1,
  usersLoading: false,
  users: {
    data: [],
    meta: {
      pageCount: 0,
      recordCount: 0,
    },
    links: {
      first: "",
      last: "",
    },
    included: [],
  },
  distributionListsPage: 1,
  distributionListsLoading: false,
  distributionLists: {
    data: [],
    meta: {
      pageCount: 0,
      recordCount: 0,
    },
    links: {
      first: "",
      last: "",
    },
    included: [],
  },
};

export default (state = initialState, action: DrawerAction) => {
  switch (action.type) {
    case LOAD_DRAWER_USERS: {
      return {
        ...state,
        usersLoading: initialState.usersLoading,
        usersPage: initialState.usersPage,
        users: {
          ...state.users,
          data: action.payload.data,
          included: action.payload.included,
          meta: action.payload.meta || state.users.meta,
          links: action.payload.links,
        },
      };
    }
    case LOAD_MORE_DRAWER_USERS: {
      return {
        ...state,
        users: {
          ...state.users,
          data: [...state.users.data, ...action.payload.data],
          included: [...state.users.included, ...action.payload.included],
          meta: action.payload.meta || state.users.meta,
          links: action.payload.links,
        },
      };
    }
    case LOAD_DRAWER_DISTRIBUTION_LISTS: {
      return {
        ...state,
        distributionListsLoading: initialState.distributionListsLoading,
        distributionListsPage: initialState.distributionListsPage,
        distributionLists: {
          ...state.distributionLists,
          data: action.payload.data,
          included: action.payload.included,
          meta: action.payload.meta || state.distributionLists.meta,
          links: action.payload.links,
        },
      };
    }
    case LOAD_MORE_DRAWER_DISTRIBUTION_LISTS: {
      return {
        ...state,
        distributionLists: {
          ...state.distributionLists,
          data: [...state.distributionLists.data, ...action.payload.data],
          included: [
            ...state.distributionLists.included,
            ...action.payload.included,
          ],
          meta: action.payload.meta || state.distributionLists.meta,
          links: action.payload.links,
        },
      };
    }
    case SET_DRAWER_USERS_LOADING: {
      return {
        ...state,
        usersLoading: action.payload,
      };
    }
    case SET_DRAWER_DISTRIBUTION_LISTS_LOADING: {
      return {
        ...state,
        distributionListsLoading: action.payload,
      };
    }
    case SET_DRAWER_USERS_PAGE: {
      return {
        ...state,
        usersPage: action.payload,
      };
    }
    case SET_DRAWER_DISTRIBUTION_LISTS_PAGE: {
      return {
        ...state,
        distributionListsPage: action.payload,
      };
    }
    default:
      return state;
  }
};
